import React, { useEffect, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Badge, Button, Card, Col, Row } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getToken } from "../services/authorize";
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from "../contexts/LoadingContext";

const API = process.env.REACT_APP_API;
const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api').replace(/\/api\/?$/, '').replace(/\/$/, '');

const resolveUploadUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path) || path.startsWith('blob:')) return path;
  return `${UPLOAD_BASE}${path.startsWith('/') ? path : `/${path}`}`;
};

const GEN_LABEL = { m: 'ชาย', f: 'หญิง' };
const PARENT_STATUS_LABEL = { togather: 'อยู่ด้วยกัน', separated: 'แยกกันอยู่', divorce: 'หย่าร้าง' };

// แสดงค่าแบบอ่านอย่างเดียว ถ้าไม่มีค่าให้แสดง "-"
const Field = ({ label, value, col = 4, labelCol = 2 }) => (
  <>
    <div className={`col-sm-${labelCol} text-sm-end text-secondary fs-sm mb-1 mb-sm-0`}>{label}</div>
    <div className={`col-sm-${col} mb-2`}>
      <span className="fw-medium" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
        {value !== null && value !== undefined && String(value).trim() !== '' ? value : <span className="text-secondary">-</span>}
      </span>
    </div>
  </>
);

const SectionTitle = ({ children }) => (
  <>
    <hr className="my-4" />
    <h6 className="fw-semibold mb-3">{children}</h6>
  </>
);

export default function StudentInfoView() {
  const { studentID } = useParams();
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);

  const [data, setData] = useState(null);

  useEffect(() => {
    setLoading(true);
    axiosInstance
      .get(`${API}/getStudentInfo/${studentID}`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((res) => {
        const normalized = Object.fromEntries(
          Object.entries(res.data || {}).map(([k, v]) => [k, v ?? ''])
        );
        setData(normalized);
      })
      .catch((error) => {
        console.error("There was an error fetching the student info!", error);
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถดึงข้อมูลนักเรียนได้ กรุณาลองใหม่อีกครั้ง',
        });
      })
      .finally(() => setLoading(false));
  }, [studentID, setLoading]);

  const photo = resolveUploadUrl(data?.st_photo);

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">หัวหน้าวิชาการ</Link></li>
              <li className="breadcrumb-item"><Link to="/academic/student-list-by-room">รายชื่อนักเรียนรายห้อง</Link></li>
              <li className="breadcrumb-item active" aria-current="page">ข้อมูลนักเรียน</li>
            </ol>
            <h4 className="main-title mb-0">ข้อมูลนักเรียน</h4>
          </div>
          <div className="mt-3 mt-md-0">
            <Button variant="outline-primary" onClick={() => navigate(-1)}>
              <i className="ri-arrow-left-line me-1"></i>ย้อนกลับ
            </Button>
          </div>
        </div>

        <Row className="g-12 mb-4">
          <Col xl="12">
            <Card className="card-one">
              <Card.Header>
                <Card.Title as="h6" className="mb-0">
                  ข้อมูลประวัติส่วนตัวนักเรียน
                  <Badge bg="secondary" className="ms-2">อ่านอย่างเดียว</Badge>
                </Card.Title>
              </Card.Header>
              <Card.Body className="p-3 p-lg-4">
                {/* หัวข้อมูล + รูป */}
                <div className="d-flex flex-column flex-md-row align-items-center align-items-md-start gap-3 mb-4">
                  <div
                    className="border bg-light d-flex align-items-center justify-content-center overflow-hidden"
                    style={{ width: 'min(100%, 160px)', aspectRatio: '3 / 4', borderRadius: 8 }}
                  >
                    {photo ? (
                      <img src={photo} alt={data?.st_name || 'student'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div className="text-center text-secondary px-3">
                        <i className="ri-user-line fs-1 d-block mb-1"></i>
                        <span className="fs-sm">ไม่มีรูป</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-grow-1 text-center text-md-start">
                    <div className="fw-semibold fs-5 mb-1">{data?.st_name || '-'}</div>
                    <div className="text-secondary mb-2">รหัสประจำตัว {data?.st_idnumber || '-'}</div>
                    <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-md-start">
                      {data?.st_gen && <Badge bg="light" text="dark">เพศ: {GEN_LABEL[data.st_gen] || data.st_gen}</Badge>}
                      {data?.st_blood && <Badge bg="light" text="dark">หมู่เลือด: {data.st_blood}</Badge>}
                      {data?.st_religion && <Badge bg="light" text="dark">ศาสนา: {data.st_religion}</Badge>}
                    </div>
                  </div>
                </div>

                {/* ข้อมูลส่วนตัว */}
                <SectionTitle>ข้อมูลส่วนตัว</SectionTitle>
                <div className="row">
                  <Field label="เลขประจำตัวประชาชน" value={data?.st_idcard} />
                  <Field label="วัน เดือน ปี เกิด" value={data?.st_birthdate} />
                  <Field label="สัญชาติ" value={data?.st_nationality} />
                  <Field label="เชื้อชาติ" value={data?.st_race} />
                  <Field label="ศาสนา" value={data?.st_religion} />
                  <Field label="หมู่เลือด" value={data?.st_blood} />
                </div>

                {/* ข้อมูลพี่น้อง */}
                <SectionTitle>ข้อมูลพี่น้อง</SectionTitle>
                <div className="row">
                  <Field label="เป็นบุตรคนที่" value={data?.st_childnumber} />
                  <Field label="พี่น้องร่วมสายโลหิต" value={data?.st_sibling ? `${data.st_sibling} คน` : ''} />
                  <Field label="ชาย" value={data?.st_malesibling ? `${data.st_malesibling} คน` : ''} />
                  <Field label="หญิง" value={data?.st_femalesibling ? `${data.st_femalesibling} คน` : ''} />
                </div>

                {/* ที่อยู่ */}
                <SectionTitle>ที่อยู่</SectionTitle>
                <div className="row">
                  <Field label="เลขรหัสประจำบ้าน" value={data?.st_homeID} />
                  <Field label="ภูมิลำเนาเดิม" value={data?.st_oldaddress} />
                  <Field label="ตำบล" value={data?.st_oldSubDistrict} />
                  <Field label="อำเภอ" value={data?.st_oldDistrict} />
                  <Field label="จังหวัด" value={data?.st_oldProvince} />
                  <Field label="รหัสไปรษณีย์" value={data?.st_oldZipcode} />
                  <Field label="ที่อยู่ปัจจุบัน" value={data?.st_current} />
                  <Field label="ตำบล" value={data?.st_currentSubDistrict} />
                  <Field label="อำเภอ" value={data?.st_currentDistrict} />
                  <Field label="จังหวัด" value={data?.st_currentProvince} />
                  <Field label="รหัสไปรษณีย์" value={data?.st_zipcode} />
                </div>

                {/* ข้อมูลบิดา */}
                <SectionTitle>ข้อมูลบิดา</SectionTitle>
                <div className="row">
                  <Field label="ชื่อ" value={data?.st_fatherName} />
                  <Field label="เลขประจำตัวประชาชน" value={data?.st_fatherIdCard} />
                  <Field label="วัน เดือน ปี เกิด" value={data?.st_fatherBirthdate} />
                  <Field label="อาชีพ" value={data?.st_fatherJob} />
                  <Field label="รายได้/ปี" value={data?.st_fatherAnnualIncome} />
                  <Field label="เบอร์โทร" value={data?.st_fatherPhone} />
                  <Field label="ที่อยู่" value={data?.st_fatherAddress} />
                  <Field label="ตำบล" value={data?.st_fatherSubDistrict} />
                  <Field label="อำเภอ" value={data?.st_fatherDistrict} />
                  <Field label="จังหวัด" value={data?.st_fatherProvince} />
                  <Field label="รหัสไปรษณีย์" value={data?.st_fatherZipcode} />
                </div>

                {/* ข้อมูลมารดา */}
                <SectionTitle>ข้อมูลมารดา</SectionTitle>
                <div className="row">
                  <Field label="ชื่อ" value={data?.st_motherName} />
                  <Field label="เลขประจำตัวประชาชน" value={data?.st_motherIdCard} />
                  <Field label="วัน เดือน ปี เกิด" value={data?.st_motherBirthdate} />
                  <Field label="อาชีพ" value={data?.st_motherJob} />
                  <Field label="รายได้/ปี" value={data?.st_motherAnnualIncome} />
                  <Field label="เบอร์โทร" value={data?.st_motherPhone} />
                  <Field label="ที่อยู่" value={data?.st_motherAddress} />
                  <Field label="ตำบล" value={data?.st_motherSubDistrict} />
                  <Field label="อำเภอ" value={data?.st_motherDistrict} />
                  <Field label="จังหวัด" value={data?.st_motherProvince} />
                  <Field label="รหัสไปรษณีย์" value={data?.st_motherZipcode} />
                </div>

                {/* สถานะและผู้ปกครอง */}
                <SectionTitle>สถานะและผู้ปกครอง</SectionTitle>
                <div className="row">
                  <Field label="รายได้เฉลี่ยครอบครัว/ปี" value={data?.st_averageAnnualIncome} />
                  <Field label="สถานะบิดามารดา" value={PARENT_STATUS_LABEL[data?.st_parentStatus] || ''} />
                  <Field label="ความเกี่ยวข้อง" value={data?.st_parentRelation} />
                  <Field label="ชื่อผู้ปกครอง" value={data?.st_parentname} />
                  <Field label="เลขประจำตัว ผู้ปกครอง" value={data?.st_parentIdCard} />
                  <Field label="วัน เดือน ปี เกิด" value={data?.st_parentBirthdate} />
                  <Field label="อาชีพ" value={data?.st_parentjob} />
                  <Field label="รายได้/ปี" value={data?.st_parentannualincome} />
                  <Field label="เบอร์โทร" value={data?.st_parentPhone} />
                  <Field label="ที่อยู่" value={data?.st_parentAddress} />
                  <Field label="ตำบล" value={data?.st_parentSubDistrict} />
                  <Field label="อำเภอ" value={data?.st_parentDistrict} />
                  <Field label="จังหวัด" value={data?.st_parentProvince} />
                  <Field label="รหัสไปรษณีย์" value={data?.st_parentZipcode} />
                  <Field label="ผู้ออกค่าใช้จ่าย" value={data?.st_educationsupporter} />
                </div>

                {/* ข้อมูลอื่นๆ */}
                <SectionTitle>ข้อมูลอื่นๆ</SectionTitle>
                <div className="row">
                  <Field label="กีฬาที่สนใจ" value={data?.st_favoritesport} />
                  <Field label="วิชาที่ชอบ" value={data?.st_favoritesubject} />
                  <Field label="แนวทางการศึกษาต่อ" value={data?.st_futurestudy} />
                  <Field label="อาชีพในอนาคต" value={data?.st_futurejob} />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Footer />
      </div>
    </React.Fragment>
  );
}
